import Joi from '@hapi/joi';
import { LanguageInfo } from 'types';

import { FormValues } from 'components/templates/form-mask';

import validate from './validate';

export const baseSchema = Joi.object<FormValues>({
  defaultHeadband: Joi.string().allow('').max(100).messages({
    'string.max': 'STRING_MAX',
  }),
  mailEnabledByDefault: Joi.boolean(),
  smsEnabledByDefault: Joi.boolean(),
  flashEnabledByDefault: Joi.boolean(),
  whatsAppEnabledByDefault: Joi.boolean(),
  smsFile: Joi.object(),
  whatsappRefFile: Joi.object(),
  whatsappMaskFile: Joi.object(),
});

const createFileValidation = (isRequired: boolean) => {
  return isRequired ? Joi.object().required().messages({ 'any.required': 'REQUIRED_FIELD' }) : Joi.object();
};

const createMailDefaultObjectValidation = (isRequired: boolean) => {
  return isRequired
    ? Joi.string().required().max(120).messages({
        'any.required': 'REQUIRED_FIELD',
        'string.empty': 'REQUIRED_FIELD',
        'string.max': 'STRING_MAX',
      })
    : Joi.string().allow('').max(120).messages({
        'string.max': 'STRING_MAX',
      });
};

// Check if all mail files have the same state (all empty or all filled)
const areMailFilesConsistent = (values: FormValues, translationLanguages: LanguageInfo[]): boolean => {
  const hasMainFile = !!values.mailFile || !!values.mailFileNameSaved;
  const translationFilesState = translationLanguages.map(
    (lang) => !!values[`${lang.code}-mailFile`] || !!values[`${lang.code}-mailFileNameSaved`]
  );

  return (
    (!hasMainFile && translationFilesState.every((hasFile) => !hasFile)) ||
    (hasMainFile && translationFilesState.every((hasFile) => hasFile))
  );
};

// Check if all mail DefaultObject have the same state (all empty or all filled)
const areMailDefaultObjectsConsistent = (values: FormValues, translationLanguages: LanguageInfo[]): boolean => {
  const hasMain = !!values.mailDefaultObject;
  const translationState = translationLanguages.map((lang) => !!values[`${lang.code}-mailDefaultObject`]);

  return (
    (!hasMain && translationState.every((hasValue) => !hasValue)) ||
    (hasMain && translationState.every((hasValue) => hasValue))
  );
};

export const createSchema = (translationLanguages: LanguageInfo[], values: FormValues) => {
  const isMailFilesConsistent = areMailFilesConsistent(values, translationLanguages);
  const mailFilesValidation = createFileValidation(!isMailFilesConsistent);

  const isMailDefaultObjectsConsistent = areMailDefaultObjectsConsistent(values, translationLanguages);
  const mailDefaultObjectValidation = createMailDefaultObjectValidation(!isMailDefaultObjectsConsistent);

  const dynamicKeys = {
    mailDefaultObject: mailDefaultObjectValidation,
    mailFile: mailFilesValidation,

    // Add mailConfig translation specific validations
    ...Object.fromEntries(translationLanguages.map((lang) => [`${lang.code}-mailFile`, mailFilesValidation])),
    ...Object.fromEntries(
      translationLanguages.map((lang) => [`${lang.code}-mailDefaultObject`, mailDefaultObjectValidation])
    ),
  };

  return baseSchema.keys(dynamicKeys);
};

export default (values: FormValues, translationLanguages: LanguageInfo[]) => {
  return validate(createSchema(translationLanguages, values), values, {
    allowUnknown: true,
    context: {},
  });
};
